import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import {
  Box, Typography, TextField, Grid, Paper, Button, Tab, CircularProgress, 
  Divider, Alert, InputAdornment, IconButton
} from '@mui/material';

import TabContext from '@mui/lab/TabContext';
import TabList from '@mui/lab/TabList';
import TabPanel from '@mui/lab/TabPanel';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import SaveIcon from '@mui/icons-material/Save';
import MonitorHeartIcon from '@mui/icons-material/MonitorHeart';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs from 'dayjs';

const API_URL = process.env.REACT_APP_API_URL || 'https://luiatro-health-cloud-production.up.railway.app';

function MedicalHistoryPage2() {
  const { patientId } = useParams(); 
  const navigate = useNavigate();
  const { user } = useAuth(); 
  
  const [patient, setPatient] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [saveMessage, setSaveMessage] = useState('');

  // ESTADO DE SIGNOS VITALES
  const [newEvolution, setNewEvolution] = useState({
    date: dayjs(), 
    weight: '',
    height: '',
    bmi: '',
    systolic: '',
    diastolic: '',
    heartRate: '',
    temperature: ''
  });

  // 1. OBTENER TOKEN (Helper simple)
  // Asumo que guardas el token en localStorage al hacer login. 
  // Si usas cookies, el navegador lo envía solo, pero tu backend busca el header Authorization.
  const getToken = () => localStorage.getItem('token'); 

  // CARGA DE DATOS BÁSICOS
  useEffect(() => {
    const fetchData = async () => {
      setIsLoading(true);
      try {
        const token = getToken();
        // 2. AGREGAR HEADER AUTHORIZATION AQUÍ
        const resPatient = await fetch(`${API_URL}/api/patients/${patientId}`, {
            headers: {
                'Authorization': `Bearer ${token}`,
                'Content-Type': 'application/json'
            }
        });

        if (resPatient.status === 401) {
            alert("Sesión expirada. Por favor inicie sesión nuevamente.");
            navigate('/login');
            return;
        }

        if (!resPatient.ok) throw new Error('Paciente no encontrado');
        const data = await resPatient.json();
        setPatient(data);
      } catch (error) { console.error(error); } finally { setIsLoading(false); }
    };
    if (patientId) fetchData();
  }, [patientId, navigate]);

  // CÁLCULO AUTOMÁTICO DE IMC
  useEffect(() => {
      const w = parseFloat(newEvolution.weight);
      const h = parseFloat(newEvolution.height); 
      if (w > 0 && h > 0) {
          const heightM = h > 3 ? h / 100 : h;
          const bmiVal = (w / (heightM * heightM)).toFixed(2);
          setNewEvolution(prev => ({ ...prev, bmi: bmiVal }));
      }
  }, [newEvolution.weight, newEvolution.height]);

  const handleSaveVitals = async () => {
    const isMedicalUser = user?.role === 'medico' || user?.role === 'administrador';

    const dataToSubmit = {
      date: new Date(newEvolution.date),
      evolutionText: user?.role === 'asistente'
        ? 'Signos vitales registrados por personal de asistencia.'
        : newEvolution.evolutionText,
      weight: newEvolution.weight ? parseFloat(newEvolution.weight) : null,
      height: newEvolution.height ? parseFloat(newEvolution.height) : null,
      bmi: newEvolution.bmi ? parseFloat(newEvolution.bmi) : null,
      systolic: newEvolution.systolic ? parseInt(newEvolution.systolic) : null,
      diastolic: newEvolution.diastolic ? parseInt(newEvolution.diastolic) : null,
      heartRate: newEvolution.heartRate ? parseInt(newEvolution.heartRate) : null,
      temperature: newEvolution.temperature ? parseFloat(newEvolution.temperature) : null,
      patientId: patientId,
      doctorId: isMedicalUser ? user.id : null,
      createdByRole: user?.role
    };

    try {
        const token = getToken();
        // 3. AGREGAR HEADER AUTHORIZATION TAMBIÉN AQUÍ
        const res = await fetch(`${API_URL}/api/evolutions`, { 
            method: 'POST', 
            headers: { 
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${token}` // <--- CLAVE DEL ERROR
            }, 
            body: JSON.stringify(dataToSubmit) 
        });

        if (res.status === 401) {
            alert("No autorizado. Verifique su sesión.");
            return;
        }

        if (res.ok) {
            setSaveMessage("Signos vitales registrados correctamente.");
            setNewEvolution({ date: dayjs(), weight: '', height: '', bmi: '', systolic: '', diastolic: '', heartRate: '', temperature: '' });
            setTimeout(() => navigate('/patients'), 2000); 
        } else {
            alert("Error al guardar datos");
        }
    } catch (e) { alert("Error al conectar con el servidor"); }
  };

  if (isLoading) return <Box sx={{display:'flex', justifyContent:'center', p:5}}><CircularProgress /></Box>;

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <Box sx={{ p: 3 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Box>
              <Typography variant="h5" color="primary" sx={{ fontWeight: 'bold' }}>TRIAGE: {patient?.name} {patient?.lastName}</Typography>
              <Typography variant="subtitle2" color="text.secondary">C.I.: {patient?.cedula || patient?.idNumber}</Typography>
          </Box>
          <IconButton onClick={() => navigate('/patients')}><ArrowBackIcon /></IconButton>
        </Box>

        {saveMessage && <Alert severity="success" sx={{ mb: 2 }}>{saveMessage}</Alert>}

        <Paper sx={{ width: '100%', mt: 2 }}>
          <TabContext value="5">
            <Box sx={{ borderBottom: 1, borderColor: 'divider', bgcolor: '#e3f2fd' }}>
              <TabList>
                <Tab label="REGISTRO DE SIGNOS VITALES" value="5" icon={<MonitorHeartIcon />} iconPosition="start" />
              </TabList>
            </Box>

            <TabPanel value="5">
              <Grid container spacing={3} justifyContent="center">
                <Grid item xs={12} md={8}>
                    <Paper variant="outlined" sx={{ p: 4, bgcolor: '#fdfdfd' }}>
                        <Typography variant="h6" gutterBottom color="primary">Captura de Datos Antropométricos</Typography>
                        <Divider sx={{ mb: 3 }} />
                        
                        <Grid container spacing={3}>
                            <Grid item xs={12} md={6}><DatePicker label="Fecha de Toma" value={newEvolution.date} onChange={(val) => setNewEvolution(p => ({ ...p, date: val }))} slotProps={{ textField: { fullWidth: true } }} /></Grid>
                            <Grid item xs={12} md={6}><TextField label="Peso (Kg)" type="number" fullWidth value={newEvolution.weight} onChange={(e) => setNewEvolution(p => ({...p, weight: e.target.value}))} /></Grid>
                            <Grid item xs={12} md={6}><TextField label="Altura (cm)" type="number" fullWidth value={newEvolution.height} onChange={(e) => setNewEvolution(p => ({...p, height: e.target.value}))} /></Grid>
                            <Grid item xs={12} md={6}><TextField label="IMC" fullWidth value={newEvolution.bmi} disabled InputProps={{startAdornment: <InputAdornment position="start">Índice</InputAdornment>}} /></Grid>
                            
                            <Grid item xs={12}><Divider sx={{ my: 1 }}><Typography variant="caption">TENSIÓN Y FRECUENCIA</Typography></Divider></Grid>
                            
                            <Grid item xs={6} md={3}><TextField label="Sistólica" type="number" fullWidth value={newEvolution.systolic} onChange={(e) => setNewEvolution(p => ({...p, systolic: e.target.value}))} /></Grid>
                            <Grid item xs={6} md={3}><TextField label="Diastólica" type="number" fullWidth value={newEvolution.diastolic} onChange={(e) => setNewEvolution(p => ({...p, diastolic: e.target.value}))} /></Grid>
                            <Grid item xs={6} md={3}><TextField label="F. Cardíaca" type="number" fullWidth value={newEvolution.heartRate} onChange={(e) => setNewEvolution(p => ({...p, heartRate: e.target.value}))} /></Grid>
                            <Grid item xs={6} md={3}><TextField label="Temp (°C)" type="number" fullWidth value={newEvolution.temperature} onChange={(e) => setNewEvolution(p => ({...p, temperature: e.target.value}))} /></Grid>
                            
                            <Grid item xs={12} sx={{ mt: 2 }}>
                                <Button 
                                    variant="contained" 
                                    size="large" 
                                    fullWidth 
                                    onClick={handleSaveVitals} 
                                    startIcon={<SaveIcon />}
                                    disabled={!newEvolution.weight && !newEvolution.systolic}
                                >
                                    Guardar Signos Vitales y Finalizar
                                </Button>
                            </Grid>
                        </Grid>
                    </Paper>
                </Grid>
              </Grid>
            </TabPanel>
          </TabContext>
        </Paper>
      </Box>
    </LocalizationProvider>
  );
}

export default MedicalHistoryPage2;